import React from 'react';
import classnames from 'classnames';
import { injectIntl, IntlShape } from 'react-intl';
import DefaultButton from '../../../../global/default-button/default-button';
import { IMeetingDetailsDTO, MeetingStatus } from '../../../../../api/meetings.dao';
import './meeting-list-item.scss';
import { AttendeeColumn } from '../../../../global/attendee-column/attendee-column';
import Text from '../../../../global/text/text';
import { IAttendeeOrganizationDTO } from '../../../../../api/attendees.dao';
import { getDisabled } from '../../../../../infrastructure/utils/attendee.utils';
import { getCompleteRoomName } from '../../../../../infrastructure/utils/common.utils';
import MeetingDateTimeColumn from '../../../../global/meeting-date-time-column/meeting-date-time-column';
import { AttendeeColumnPopoverViewmodel } from '../../../../global/attendee-column/attendee-column-popover/attendee-column-popover.viewmodel';
import OptionsButton from '../../../../global/options-button/options-button';
import { GENERATE_REPORT_BUTTON_LABEL, INTERACTIONS_REPORT_LABEL, REGISTRATION_REPORT_LABEL } from '../../_control/_control-list/_control-list-item/_control-list-item-utils';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../../api/roles.dao';
import { IReportGenerationNotificationProps } from '../../_reports/show-report-toast/show-report-toast';
import { observer } from 'mobx-react';

interface IProps extends IMeetingDetailsDTO {
    intl: IntlShape;
    handleEditClick: (meetingId: number) => void;
    eventTimezone: string;
    isStartEndTimeRenderedInPreviousRow: boolean;
    isSelected: boolean;
    isFirstItemInDayGroup: boolean;
    searchQuery?: string;
    accountId: number;
    eventId: number;
    handleRemoveAttendeeClick?: (attendeeId: number, organizationId: number, key: string) => void;
    handleAttendeesChange?: (attendees: IAttendeeOrganizationDTO[], key: string) => void;
    attendees: IAttendeeOrganizationDTO[];
    createAttendeeFromForm?: (keyRole) => void;
    fetchAttendeeList: () => void;
    isEditable?: boolean;
    loadingAttendees: boolean;
    resetLoadingState: () => void;
    attendeeDetailsPopoverVM: AttendeeColumnPopoverViewmodel;
    generateMeetingRegistrationReport?: (
        meetingId: number,
        reportGenerationNotificationProps: IReportGenerationNotificationProps,
    ) => void;
    generateMeetingInteractionsReport?: (
        meetingId: number,
        reportGenerationNotificationProps: IReportGenerationNotificationProps,
    ) => void;
}

const MeetingListItem = injectIntl(observer((props: IProps) => {
    const {
        intl,
        startDateTime,
        endDateTime,
        eventTimezone,
        meetingId,
        isSelected,
        status,
        isFirstItemInDayGroup,
        isStartEndTimeRenderedInPreviousRow,
        presenters,
        hosts,
        participants,
        registrants,
        supports,
        room,
        handleEditClick,
        searchQuery,
        accountId,
        eventId,
        handleRemoveAttendeeClick,
        createAttendeeFromForm,
        handleAttendeesChange,
        attendees,
        fetchAttendeeList,
        isEditable,
        type,
        topic,
        loadingAttendees,
        resetLoadingState,
        attendeeDetailsPopoverVM,
        generateMeetingRegistrationReport,
        generateMeetingInteractionsReport,
    } = props;

    const reportGenerationNotificationProps = {
        startDateTime: startDateTime,
        topic: topic,
        presentingOrganizationNames: presenters?.map(org => org.name),
    };

    const reportOptions = [
        hasPermission(Permission.MEETING_REGISTRATION_REPORT) && {
            label: intl.formatMessage(REGISTRATION_REPORT_LABEL),
            action: () => generateMeetingRegistrationReport(
                meetingId,
                reportGenerationNotificationProps
            )
        },
        hasPermission(Permission.MEETING_INTERACTIONS_REPORT) && {
            label: intl.formatMessage(INTERACTIONS_REPORT_LABEL),
            action: () => generateMeetingInteractionsReport(
                meetingId,
                reportGenerationNotificationProps
            ),
        },
    ].filter(Boolean);

    const onEditClick = () => handleEditClick(meetingId);

    const mergeWithPreviousRowClass = classnames({ 'merge-with-previous-row': isFirstItemInDayGroup });

    const cellEffectClasses = classnames(mergeWithPreviousRowClass, {
        'active': isSelected,
        'hoverable': !isSelected,
    });

    const scheduleCancelledClass = classnames({ 'schedule--cancelled': status === MeetingStatus.Cancelled });
    const meetingIdToString = meetingId.toString();

    return (
        <tr className='meeting-list-item' onDoubleClick={isEditable ? onEditClick : undefined}>
            <MeetingDateTimeColumn
                startDateTime={startDateTime}
                endDateTime={endDateTime}
                eventTimezone={eventTimezone}
                isStartEndTimeRenderedInPreviousRow={isStartEndTimeRenderedInPreviousRow}
                mergeWithPreviousRowClass={mergeWithPreviousRowClass}
            />
            <td className={classnames('schedule', cellEffectClasses, scheduleCancelledClass)}>
                <AttendeeColumn
                    organizations={presenters}
                    searchValue={searchQuery}
                    withAttendeePopover={true}
                    accountId={accountId}
                    eventId={eventId}
                    withRemove={isEditable}
                    handleRemoveAttendeeClick={(attendeeId, organizationId) => handleRemoveAttendeeClick(attendeeId, organizationId, 'presenters')}
                    withAddButton={isEditable}
                    addButtonText={'+ Add Presenters'}
                    handleAttendeesChange={(res: IAttendeeOrganizationDTO[]) => handleAttendeesChange(res, 'presenters')}
                    createAttendeeFromForm={() => createAttendeeFromForm('presenters')}
                    intl={intl}
                    fetchAttendeeList={fetchAttendeeList}
                    allAttendees={attendees}
                    disabled={getDisabled([
                        ...presenters,
                        ...participants,
                        ...hosts,
                        ...registrants,
                        ...supports
                    ])}
                    loadingAttendees={loadingAttendees}
                    resetLoadingState={resetLoadingState}
                    additionalAttendeeId={meetingIdToString}
                    attendeeDetailsPopoverVM={attendeeDetailsPopoverVM}
                />
            </td>
            <td className={classnames('schedule', cellEffectClasses, scheduleCancelledClass)}>
                <AttendeeColumn
                    organizations={participants}
                    searchValue={searchQuery}
                    withAttendeePopover={true}
                    accountId={accountId}
                    eventId={eventId}
                    withRemove={isEditable}
                    handleRemoveAttendeeClick={(attendeeId, organizationId) => handleRemoveAttendeeClick(attendeeId, organizationId, 'participants')}
                    withAddButton={isEditable}
                    addButtonText={'+ Add Participants'}
                    handleAttendeesChange={(res: IAttendeeOrganizationDTO[]) => handleAttendeesChange(res, 'participants')}
                    createAttendeeFromForm={() => createAttendeeFromForm('participants')}
                    intl={intl}
                    allAttendees={attendees}
                    fetchAttendeeList={fetchAttendeeList}
                    disabled={getDisabled([
                        ...presenters,
                        ...participants,
                        ...hosts,
                        ...registrants,
                        ...supports
                    ])}
                    loadingAttendees={loadingAttendees}
                    resetLoadingState={resetLoadingState}
                    additionalAttendeeId={meetingIdToString}
                    attendeeDetailsPopoverVM={attendeeDetailsPopoverVM}
                />
            </td>
			<td className={classnames('schedule', cellEffectClasses, scheduleCancelledClass)}>
                <AttendeeColumn
                    organizations={hosts}
                    searchValue={searchQuery}
                    withAttendeePopover={true}
                    accountId={accountId}
                    eventId={eventId}
                    withRemove={isEditable}
                    handleRemoveAttendeeClick={(attendeeId, organizationId) => handleRemoveAttendeeClick(attendeeId, organizationId, 'hosts')}
                    withAddButton={isEditable}
                    addButtonText={'+ Add Hosts'}
                    handleAttendeesChange={(res: IAttendeeOrganizationDTO[]) => handleAttendeesChange(res, 'hosts')}
                    createAttendeeFromForm={() => createAttendeeFromForm('hosts')}
                    intl={intl}
                    allAttendees={attendees}
                    fetchAttendeeList={fetchAttendeeList}
                    disabled={getDisabled([
                        ...presenters,
                        ...participants,
                        ...hosts,
                        ...registrants,
                        ...supports
                    ])}
                    loadingAttendees={loadingAttendees}
                    resetLoadingState={resetLoadingState}
                    additionalAttendeeId={meetingIdToString}
                    attendeeDetailsPopoverVM={attendeeDetailsPopoverVM}
                />
            </td>
            <td className={classnames('schedule', 'column-with-filters', cellEffectClasses, scheduleCancelledClass)}>
                <div className='meeting-list-item__header meeting-list-item__word-break'>{type}</div>
            </td>
            <td className={classnames('schedule', cellEffectClasses, scheduleCancelledClass)}>
                <Text
                    className='meeting-list-item__header meeting-list-item__word-break'
                    message={topic}
                    highlightValue={searchQuery}
                    withHighlight
                />
            </td>
            <td className={classnames('schedule', 'column-with-filters',cellEffectClasses, scheduleCancelledClass)}>
                <div className='meeting-list-item__header'>
                    {room ? (
                        <Text message={getCompleteRoomName({ name: room.roomName, label: room.roomLabel })} withEllipsis />
                    ) : (
                        <Text
                            tag='div'
                            data-testid='unassigned-room'
                            className='meeting-list-item__text meeting-list-item__text--unassigned-room'
                            id='oe.common.unassigned'
                            message='Unassigned'
                            description='Label text'
                        />
                    )}
                </div>
            </td>
            <td className={classnames('schedule', 'align-right', cellEffectClasses)}>
                <div className='meeting-list-item__buttons-wrapper'>
                    {isEditable && <DefaultButton
                        className='visible-while-hovered'
                        modifier='slim'
                        text={intl.formatMessage({
                            id: 'oe.common.edit',
                            defaultMessage: 'Edit',
                            description: 'Button text'
                        })}
                        onClick={onEditClick}
                    />}
                    {!!reportOptions.length && <OptionsButton
                        className='visible-while-hovered generate-report-button'
                        modifier={['slim', 'options']}
                        label={intl.formatMessage(GENERATE_REPORT_BUTTON_LABEL)}
                        options={reportOptions}
                    />}
                </div>
            </td>
        </tr>
    );
}));

export { MeetingListItem };
